import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";

/* ================= ROUTE GUARDS ================= */
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./admin/routes/AdminRoute";
import PublicLayout from "./layouts/PublicLayout";

/* ================= STUDENT PAGES ================= */
const Home = lazy(() => import("./pages/Home"));
const CoursesPage = lazy(() => import("./pages/CoursesPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const LogoutPage = lazy(() => import("./pages/LogoutPage"));

/* 🔥 CLASS FLOW */
const ClassPage = lazy(() => import("./pages/ClassPage"));
const NotesPage = lazy(() => import("./pages/NotesPage"));
const McqPage = lazy(() => import("./pages/McqPage"));
const AssignmentsPage = lazy(() => import("./pages/AssignmentsPage"));
const SamplePapersPage = lazy(() => import("./pages/SamplePapersPage"));
const VideosPage = lazy(() => import("./pages/VideosPage"));

const LiveLecturesPage = lazy(() => import("./pages/LiveLecturesPage"));
const ResultsPage = lazy(() => import("./pages/ResultsPage"));
const ForumPage = lazy(() => import("./pages/ForumPage"));

/* ================= PAYMENTS ================= */
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentFail = lazy(() => import("./pages/PaymentFail"));

/* ================= ADMIN ================= */
const AdminDashboard = lazy(() => import("./admin/pages/AdminDashboard"));
const UsersPage = lazy(() => import("./admin/pages/UsersPage"));
const StatsPage = lazy(() => import("./admin/pages/StatsPage"));
const AdminPaymentsPage = lazy(() => import("./admin/pages/AdminPaymentsPage"));

const AdminNotesUpload = lazy(() => import("./admin/pages/AdminNotesUpload"));
const AdminMcqUpload = lazy(() => import("./admin/pages/AdminMcqUpload"));
const AdminSampleUpload = lazy(() => import("./admin/pages/AdminSampleUpload"));
const AdminAssignmentUpload = lazy(() => import("./admin/pages/AdminAssignmentUpload"));
const AdminVideosUpload = lazy(() => import("./admin/pages/AdminVideosUpload"));

const NotesList = lazy(() => import("./admin/pages/NotesList"));
const McqList = lazy(() => import("./admin/pages/McqList"));
const AssignmentsList = lazy(() => import("./admin/pages/AssignmentsList"));
const SamplePapersList = lazy(() => import("./admin/pages/SamplePapersList"));
const VideosList = lazy(() => import("./admin/pages/VideosList"));
const OrdersList = lazy(() => import("./admin/pages/OrdersList"));

/* ================= LEGAL ================= */
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));

/* ================= 404 ================= */
function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <h1 className="text-3xl font-bold text-red-600">
        404 – Page Not Found
      </h1>
    </div>
  );
}

/* ================= APP ================= */
function App() {
  return (
    <Router>
      <Suspense fallback={<div className="p-10 text-center">Loading…</div>}>
        <Routes>

          {/* ===== PUBLIC ===== */}
          <Route path="/" element={<Home />} />
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* ===== AUTH ===== */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/logout" element={<LogoutPage />} />

          {/* ================================================= */}
          {/* 🔥 HERO ENTRY ROUTES (CLASS SELECT FLOW) */}
          {/* ================================================= */}
          <Route path="/notes" element={<NotesPage />} />
          <Route path="/mcq" element={<McqPage />} />
          <Route path="/assignments" element={<AssignmentsPage />} />
          <Route path="/sample-papers" element={<SamplePapersPage />} />
          <Route path="/videos" element={<VideosPage />} />

          {/* ================================================= */}
          {/* 🔥 CLASS BASED ROUTING (MAIN FLOW) */}
          {/* ================================================= */}

          {/* Class landing page (4 containers) */}
          <Route path="/class/:classLevel" element={<ClassPage />} />

          {/* Notes */}
          <Route path="/class/:classLevel/notes" element={<NotesPage />} />
          <Route path="/class/:classLevel/notes/:id" element={<NotesPage />} />

          {/* MCQ */}
          <Route path="/class/:classLevel/mcq" element={<McqPage />} />

          {/* Assignments */}
          <Route
            path="/class/:classLevel/assignments"
            element={<AssignmentsPage />}
          />

          {/* Sample Papers */}
          <Route
            path="/class/:classLevel/sample-papers"
            element={<SamplePapersPage />}
          />

          {/* Videos */}
          <Route
            path="/class/:classLevel/videos"
            element={<VideosPage />}
          />

          {/* ===== PAYMENTS ===== */}
          <Route
            path="/checkout/:id"
            element={
              <ProtectedRoute>
                <CheckoutPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-success"
            element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-fail"
            element={
              <ProtectedRoute>
                <PaymentFail />
              </ProtectedRoute>
            }
          />

          {/* ===== ADMIN ===== */}
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
          <Route path="/admin/stats" element={<AdminRoute><StatsPage /></AdminRoute>} />
          <Route path="/admin/payments" element={<AdminRoute><AdminPaymentsPage /></AdminRoute>} />

          <Route path="/admin/notes/upload" element={<AdminRoute><AdminNotesUpload /></AdminRoute>} />
          <Route path="/admin/mcq/upload" element={<AdminRoute><AdminMcqUpload /></AdminRoute>} />
          <Route path="/admin/sample-papers/upload" element={<AdminRoute><AdminSampleUpload /></AdminRoute>} />
          <Route path="/admin/assignments/upload" element={<AdminRoute><AdminAssignmentUpload /></AdminRoute>} />
          <Route path="/admin/videos/upload" element={<AdminRoute><AdminVideosUpload /></AdminRoute>} />

          <Route path="/admin/notes/list" element={<AdminRoute><NotesList /></AdminRoute>} />
          <Route path="/admin/mcq/list" element={<AdminRoute><McqList /></AdminRoute>} />
          <Route path="/admin/assignments/list" element={<AdminRoute><AssignmentsList /></AdminRoute>} />
          <Route path="/admin/sample-papers/list" element={<AdminRoute><SamplePapersList /></AdminRoute>} />
          <Route path="/admin/videos/list" element={<AdminRoute><VideosList /></AdminRoute>} />
          <Route path="/admin/orders/list" element={<AdminRoute><OrdersList /></AdminRoute>} />

          {/* ===== LEGAL ===== */}
          <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
          <Route path="/terms" element={<PublicLayout><TermsConditions /></PublicLayout>} />
          <Route path="/refund-policy" element={<PublicLayout><RefundPolicy /></PublicLayout>} />
          <Route path="/disclaimer" element={<PublicLayout><Disclaimer /></PublicLayout>} />

          {/* ===== FALLBACK ===== */}
          <Route path="*" element={<NotFound />} />

        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
